import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography, Select, DatePicker, Space, Empty } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  getHistorico,
  type BrevoSyncHistoricoItem,
  type HistoricoFilters,
  formatDuracion,
} from '../../api/brevoSyncApi';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const HistorySection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BrevoSyncHistoricoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [estadoFilter, setEstadoFilter] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const filters: HistoricoFilters = {
        page,
        pageSize,
        tipo: 'diario',
        estado: estadoFilter,
        desde: dateRange?.[0]?.format('YYYY-MM-DD'),
        hasta: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const result = await getHistorico(filters);
      setData(result.ejecuciones);
      setTotal(result.total);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize, estadoFilter, dateRange]);

  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'ok': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error': return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
      case 'running': return <SyncOutlined spin style={{ color: '#1890ff' }} />;
      default: return null;
    }
  };

  const estadoTag = (estado: string, item: BrevoSyncHistoricoItem) => {
    if (estado === 'ok' && (item.faseAErrores > 0 || item.faseBErrores > 0)) {
      return <Tag color="warning" icon={<WarningOutlined />}>{item.faseAErrores + item.faseBErrores} errores</Tag>;
    }
    const map: Record<string, { color: string; label: string }> = {
      ok: { color: 'success', label: 'Correcta' },
      error: { color: 'error', label: 'Fallida' },
      running: { color: 'processing', label: 'En curso' },
    };
    const cfg = map[estado] || { color: 'default', label: estado };
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
  };

  const columns: ColumnsType<BrevoSyncHistoricoItem> = [
    {
      title: 'Fecha',
      dataIndex: 'inicio',
      key: 'fecha',
      width: 150,
      render: (val: string) => new Date(val).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    },
    {
      title: '',
      key: 'icon',
      width: 30,
      render: (_, r) => estadoIcon(r.estado),
    },
    {
      title: 'Resultado',
      key: 'resultado',
      width: 180,
      render: (_, r) => {
        if (r.estado === 'running') return estadoTag(r.estado, r);
        const total = r.faseAExitos + r.faseBExitos;
        return <span>{total.toLocaleString('es')} contactos {estadoTag(r.estado, r)}</span>;
      },
    },
    {
      title: 'Duración',
      dataIndex: 'duracionSegundos',
      key: 'duracion',
      width: 100,
      render: (val: number) => formatDuracion(val),
    },
    {
      title: 'Origen',
      dataIndex: 'origen',
      key: 'origen',
      width: 100,
      render: (val: string) => <Text type="secondary" style={{ textTransform: 'capitalize' }}>{val}</Text>,
    },
  ];

  const expandedRowRender = (record: BrevoSyncHistoricoItem) => (
    <div style={{ padding: '8px 0' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text><strong>Inicio:</strong> {new Date(record.inicio).toLocaleString('es-ES')}</Text>
        {record.fin && <Text><strong>Fin:</strong> {new Date(record.fin).toLocaleString('es-ES')}</Text>}
        <Text><strong>Fase A:</strong> {record.faseAExitos} OK, {record.faseAErrores} errores{record.faseATotal ? ` de ${record.faseATotal.toLocaleString('es')}` : ''}</Text>
        <Text><strong>Fase B:</strong> {record.faseBExitos} OK, {record.faseBErrores} errores{record.faseBTotal ? ` de ${record.faseBTotal.toLocaleString('es')}` : ''}</Text>
        <Text><strong>Duplicados fusionados:</strong> {record.duplicadosFusionados}</Text>
        {record.parametros && <Text><strong>Parámetros:</strong> {record.parametros}</Text>}
        {record.errorDetalle && (
          <div style={{ background: '#fff2f0', padding: '8px 12px', borderRadius: 4, marginTop: 4 }}>
            <Text type="danger" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{record.errorDetalle}</Text>
          </div>
        )}
      </Space>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong style={{ fontSize: 14 }}>Histórico de sincronizaciones</Text>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <RangePicker
          value={dateRange}
          onChange={(dates) => { setDateRange(dates); setPage(1); }}
          format="DD/MM/YYYY"
          placeholder={['Desde', 'Hasta']}
          size="small"
        />
        <Select
          value={estadoFilter}
          onChange={(val) => { setEstadoFilter(val); setPage(1); }}
          placeholder="Estado"
          allowClear
          size="small"
          style={{ width: 140 }}
          options={[
            { label: 'Correctas', value: 'ok' },
            { label: 'Fallidas', value: 'error' },
            { label: 'En curso', value: 'running' },
          ]}
        />
      </div>
      <Table
        dataSource={data.map(d => ({ ...d, key: d.id }))}
        columns={columns}
        loading={loading}
        expandable={{ expandedRowRender }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showSizeChanger: true,
          pageSizeOptions: ['10', '30', '50'],
          showTotal: (t) => `${t} ejecuciones`,
          size: 'small',
        }}
        size="small"
        locale={{ emptyText: <Empty description="Sin ejecuciones registradas" /> }}
      />
    </div>
  );
};

export default HistorySection;
